import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MigrationDataAccessLayer } from '../services/migrationDataAccessLayer';
import Logger from '../services/logger';
import {
  getPortalRedirectForShell,
  type PortalShell,
} from '../utils/portalAccessUtils';

/**
 * Redirects authenticated users to their correct portal when they hit the wrong layout shell.
 * Skips enforcement on allowedRoutePrefixes (e.g. onboarding).
 */
export function usePortalShellGuard(shell: PortalShell, allowedRoutePrefixes: string[] = []): void {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user?.uid) return;

    const onAllowedRoute = allowedRoutePrefixes.some((prefix) =>
      location.pathname.startsWith(prefix)
    );
    if (onAllowedRoute) return;

    const migrationDataAccess = new MigrationDataAccessLayer();

    const enforce = async () => {
      const result = await migrationDataAccess.getUserProfile(user.uid);
      if (!result.success || !result.data) return;

      const redirect = getPortalRedirectForShell(shell, result.data as Record<string, unknown>);
      if (redirect && redirect !== location.pathname) {
        Logger.warn('[PortalShellGuard] Wrong portal shell — redirecting', {
          shell,
          uid: user.uid,
          from: location.pathname,
          to: redirect,
        });
        navigate(redirect, { replace: true });
      }
    };

    void enforce();
  }, [user?.uid, shell, navigate, location.pathname, allowedRoutePrefixes]);
}
